package com.cs.ejobin.ui.screens.home

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.material3.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.cs.ejobin.i18n.Keys
import com.cs.ejobin.i18n.t
import com.cs.ejobin.model.Extra
import com.cs.ejobin.model.SkillCatalog
import com.cs.ejobin.ui.components.ScreenScaffold
import com.cs.ejobin.ui.theme.AppColors
import com.cs.ejobin.ui.theme.AppSurfaces
import com.cs.ejobin.vm.HomeViewModel

@Composable
fun HomeScreen(
    vm: HomeViewModel,
    onOpenExtra: (String) -> Unit,
    onOpenProfile: () -> Unit,
    onNeedPaywall: () -> Unit, // ✅ conservé pour ne rien casser, mais plus utilisé
    onLogout: () -> Unit
) {
    val state by vm.state.collectAsState()

    // ✅ Paiement désactivé : l’accès annuaire = user connecté (cf AppSession)
    val canSeeDirectory = state.hasDirectoryAccess

    val primaryTextButtonColors = ButtonDefaults.textButtonColors(
        contentColor = AppColors.Primary
    )

    ScreenScaffold(
        title = t("eJobin"),
        topActions = {
            TextButton(onClick = onOpenProfile, colors = primaryTextButtonColors) {
                Text(t(Keys.profile_title))
            }
            TextButton(onClick = onLogout, colors = primaryTextButtonColors) {
                Text(t(Keys.logout))
            }
        }
    ) {
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(12.dp),
            contentPadding = PaddingValues(bottom = 24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {

            // ✅ Paiement désactivé : plus de locked banner
            /*
            item {
                if (!canSeeDirectory) {
                    LockedBanner(
                        onSubscribe = onNeedPaywall,
                        isLoading = state.isRefreshing || state.isLoadingMore
                    )
                }
            }
            */

            item {
                SearchCard(
                    query = state.search,
                    onQueryChange = vm::onSearchChanged,
                    enabled = canSeeDirectory,
                    isLoading = state.isRefreshing
                )
            }

            item {
                SkillsFilterCard(
                    selected = state.selectedSkills,
                    onToggle = vm::toggleSkill,
                    onClear = vm::clearFilters,
                    enabled = canSeeDirectory
                )
            }

            if (state.isRefreshing) {
                item { LoadingBlock() }
            }

            if (state.error != null) {
                item {
                    ErrorBlock(
                        message = state.error ?: Keys.error_generic,
                        onRetry = vm::refresh
                    )
                }
            }

            if (canSeeDirectory && !state.isRefreshing && state.error == null && state.items.isEmpty()) {
                item { EmptyBlock() }
            }

            if (canSeeDirectory) {
                itemsIndexed(state.items, key = { _, e -> e.id }) { index, extra ->
                    vm.loadMoreIfNeeded(index)
                    ExtraCard(extra = extra, onClick = { onOpenExtra(extra.id) })
                }

                if (state.isLoadingMore) {
                    item {
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(12.dp),
                            horizontalArrangement = Arrangement.Center
                        ) {
                            CircularProgressIndicator()
                        }
                    }
                }
            } else {
                // ✅ Paiement désactivé : plus de locked cards
                /*
                items(8) {
                    LockedExtraCard(onClick = onNeedPaywall)
                }
                */
            }
        }
    }
}

@Composable
private fun LockedBanner(onSubscribe: () -> Unit, isLoading: Boolean) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = t(Keys.subscription_required_title),
                style = MaterialTheme.typography.titleMedium,
                color = AppColors.TextPrimary
            )
            Text(
                text = t(Keys.subscription_required_body),
                style = MaterialTheme.typography.bodyMedium,
                color = AppColors.TextPrimary
            )

            Button(
                onClick = onSubscribe,
                enabled = !isLoading,
                colors = ButtonDefaults.buttonColors(containerColor = AppColors.Primary)
            ) {
                Text(t(Keys.see_offers))
            }
        }
    }
}

@Composable
private fun SearchCard(
    query: String,
    onQueryChange: (String) -> Unit,
    enabled: Boolean,
    isLoading: Boolean
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Text(
                text = t(Keys.search),
                style = MaterialTheme.typography.titleSmall,
                color = AppColors.TextPrimary
            )

            OutlinedTextField(
                value = query,
                onValueChange = onQueryChange,
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                enabled = enabled && !isLoading,
                label = { Text(t(Keys.search_placeholder)) }
            )
        }
    }
}

@Composable
private fun SkillsFilterCard(
    selected: Set<String>,
    onToggle: (String) -> Unit,
    onClear: () -> Unit,
    enabled: Boolean
) {
    val allSkills = remember { SkillCatalog.entries.map { it.labelKey } }
    var expanded by remember { mutableStateOf(false) }

    val collapsedCount = 9
    val visibleSkills = if (expanded) allSkills else allSkills.take(collapsedCount)
    val canExpand = allSkills.size > collapsedCount

    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = t(Keys.filters),
                    style = MaterialTheme.typography.titleSmall,
                    modifier = Modifier.weight(1f),
                    color = AppColors.TextPrimary
                )

                if (selected.isNotEmpty() && enabled) {
                    TextButton(
                        onClick = onClear,
                        colors = ButtonDefaults.textButtonColors(contentColor = AppColors.Primary)
                    ) {
                        Text(t(Keys.clear_filters))
                    }
                }
            }

            val cols = 5
            visibleSkills.chunked(cols).forEach { row ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    row.forEach { skillKey ->
                        val label = SkillCatalog.fromLabelKey(skillKey)?.label ?: t(skillKey)
                        val isOn = selected.contains(label)

                        FilterChip(
                            selected = isOn,
                            enabled = enabled,
                            onClick = { onToggle(label) },
                            label = { Text(t(skillKey)) },
                            leadingIcon = if (isOn) {
                                {
                                    Icon(
                                        imageVector = Icons.Filled.Check,
                                        contentDescription = null,
                                        modifier = Modifier.size(16.dp),
                                        tint = AppColors.Primary
                                    )
                                }
                            } else null,
                            modifier = Modifier.weight(1f)
                        )
                    }
                    repeat((cols - row.size).coerceAtLeast(0)) { Spacer(Modifier.weight(1f)) }
                }
            }

            if (canExpand) {
                TextButton(
                    onClick = { expanded = !expanded },
                    colors = ButtonDefaults.textButtonColors(contentColor = AppColors.Primary),
                    enabled = enabled
                ) {
                    Text(if (expanded) t(Keys.show_less) else t(Keys.show_more))
                }
            }
        }
    }
}

@Composable
private fun ExtraCard(extra: Extra, onClick: () -> Unit) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Text(
                extra.name,
                style = MaterialTheme.typography.titleMedium,
                color = AppColors.TextPrimary
            )

            val skillsKeys = extra.skills.map { s ->
                SkillCatalog.fromLabel(s.label)?.let { t(it.labelKey) } ?: s.label
            }
            val skillsText = skillsKeys.joinToString(" • ") { it }.ifBlank { "—" }

            Text(
                text = skillsText,
                style = MaterialTheme.typography.bodyMedium,
                color = AppColors.TextPrimary
            )

            Text(
                text = if (extra.isAvailable) t(Keys.available) else t(Keys.unavailable),
                style = MaterialTheme.typography.bodySmall,
                color = if (extra.isAvailable) AppColors.Primary
                else MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun LockedExtraCard(onClick: () -> Unit) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Text(
                text = t(Keys.directory_locked_item_title),
                style = MaterialTheme.typography.titleMedium
            )
            Text(
                text = t(Keys.directory_locked_item_body),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            TextButton(
                onClick = onClick,
                colors = ButtonDefaults.textButtonColors(contentColor = AppColors.Primary)
            ) {
                Text(t(Keys.see_offers))
            }
        }
    }
}

@Composable
private fun LoadingBlock() {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
        Spacer(Modifier.width(10.dp))
        Text(t(Keys.loading))
    }
}

@Composable
private fun ErrorBlock(message: String, onRetry: () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(message)
            Button(
                onClick = onRetry,
                colors = ButtonDefaults.buttonColors(containerColor = AppColors.Primary)
            ) {
                Text(t(Keys.retry))
            }
        }
    }
}

@Composable
private fun EmptyBlock() {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(t(Keys.no_results_title), style = MaterialTheme.typography.titleMedium)
            Text(
                t(Keys.no_results_body),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}